// index.tsx
import React, { useState } from 'react';
import { format, isValid, isPast, startOfDay, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  CardContainer,
  PriorityBadge, 
  StatusBadge,
  DeadlineContainer, 
  MetadataItem,
  AssigneeAvatar,
  CountBadge,
  Footer,
  UserAttachmentsContainer,
  AttachmentsContainer
} from './styles';
import { Card as CardInterface } from '../../interfaces/board';
import { PriorityType } from '../../enums';
import { getStatusLabel, getStatusColor } from '../../enums/Status';
import { Tooltip, Box, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import AttachmentIcon from '@mui/icons-material/Attachment';
import CommentIcon from '@mui/icons-material/Comment';
import { OpenCardModal } from '../openCardModal';

interface CardProps {
  data: CardInterface;
  columnId: string;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, columnId: string, cardId: string) => void;
  onDragEnd: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>, columnId: string, cardId: string) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>, columnId: string, cardId: string) => void;
  onCardUpdate?: (updatedCard: CardInterface) => void;
  isObserver?: boolean;
}

const getPriorityLabel = (priority?: PriorityType): string => {
  switch (priority) {
    case 'high':
      return 'Alta';
    case 'medium':
      return 'Média';
    case 'low':
      return 'Baixa';
    default:
      return '';
  }
};

const formatDateString = (dateString?: string): string => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  if (!isValid(date)) return '';
  
  return format(date, 'dd/MM/yyyy', { locale: ptBR });
};

const isDatePast = (dateString?: string, status?: string): boolean => {
  if (!dateString) return false;
  
  // Cards with status 'done' should never be considered past due
  if (status?.toLowerCase() === 'done') return false;
  
  const date = new Date(dateString);
  if (!isValid(date)) return false;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Zerar horas, minutos, segundos e milisegundos
  date.setHours(0, 0, 0, 0);  // Zerar horas, minutos, segundos e milisegundos
  
  // Comparar apenas as datas sem o tempo
  return date.getTime() < today.getTime();
};

const isNearDeadline = (dateString?: string, status?: string, daysThreshold: number = 7): boolean => {
  if (!dateString) return false;
  
  // Cards with status 'done' should never be considered near deadline
  if (status?.toLowerCase() === 'done') return false;
  
  const date = new Date(dateString);
  if (!isValid(date)) return false;
  
  // Se já passou, não é "próximo" do prazo, já está vencido
  if (isDatePast(dateString, status)) return false;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Zerar horas, minutos, segundos e milisegundos
  date.setHours(0, 0, 0, 0);  // Zerar horas, minutos, segundos e milisegundos
  
  // Diferença em dias - cálculo manual para evitar problemas com a biblioteca
  const daysRemaining = Math.floor((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  
  // Está próximo se a diferença for menor ou igual ao limite de dias
  return daysRemaining <= daysThreshold;
};

const getDeadlineStatus = (dateString?: string, status?: string): 'normal' | 'warning' | 'expired' => {
  // Cards with status 'done' should always be 'normal'
  if (status?.toLowerCase() === 'done') return 'normal';
  
  if (isDatePast(dateString, status)) return 'expired';
  if (isNearDeadline(dateString, status)) return 'warning';
  return 'normal';
};

const BoardCard: React.FC<CardProps> = ({ 
  data, 
  columnId,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onCardUpdate,
  isObserver = false
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const handleCardClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleCardUpdate = (updatedCard: CardInterface) => {
    if (onCardUpdate) {
      // Se o card foi excluído
      if ((updatedCard as any)._deleted) {
        onCardUpdate({
          ...data,
          _deleted: true
        } as any);
      } else {
        // Se a prioridade foi alterada, atualizar no localStorage
        if (updatedCard.priority !== data.priority && typeof localStorage !== 'undefined') {
          try {
            const savedPrioritiesStr = localStorage.getItem('cardPriorities') || '{}';
            const cardPriorities = JSON.parse(savedPrioritiesStr);
            cardPriorities[data.id] = updatedCard.priority;
            localStorage.setItem('cardPriorities', JSON.stringify(cardPriorities));
          } catch (e) {
            console.error('Erro ao salvar prioridade no localStorage:', e);
          }
        }
        
        // Atualização normal
        onCardUpdate(updatedCard);
      }
    }
    setModalOpen(false);
  };

  // Obter a prioridade salva do localStorage, se existir
  const getSavedPriority = (cardId: string, defaultPriority?: PriorityType): PriorityType | undefined => {
    try {
      if (typeof localStorage !== 'undefined') { // Verifica se localStorage está disponível
        const savedPrioritiesStr = localStorage.getItem('cardPriorities');
        if (savedPrioritiesStr) {
          const cardPriorities = JSON.parse(savedPrioritiesStr);
          return cardPriorities[cardId] || defaultPriority;
        }
      }
      return defaultPriority;
    } catch (e) {
      console.error('Erro ao obter prioridade do localStorage:', e);
      return defaultPriority;
    }
  };

  return (
    <>
      <CardContainer
        draggable
        onDragStart={(e) => onDragStart(e, columnId, data.id)}
        onDragEnd={onDragEnd}
        onDragOver={(e) => onDragOver(e, columnId, data.id)}
        onDrop={(e) => onDrop(e, columnId, data.id)}
        onClick={handleCardClick}
        className="card-draggable"
        $statusColor={getStatusColor(data.status)}
      >
        {/* Header - Title with Date on top-right corner */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, width: '100%' }}>
          <Tooltip title={data.title}>
            <Box 
              sx={{ 
                maxWidth: 'calc(100% - 50px)', 
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '&:hover': {
                  overflow: 'hidden',
                }
              }}
            >
              <Typography 
                variant="body2" 
                sx={{
                  display: 'inline-block',
                  fontWeight: 500,
                  '&:hover': {
                    animationName: 'slide-left',
                    animationDuration: `${Math.min(Math.max(2, data.title.length / 10), 5)}s`,
                    animationTimingFunction: 'ease-in-out',
                    animationIterationCount: 'infinite',
                    animationDirection: 'alternate',
                  },
                  '@keyframes slide-left': {
                    'from': {
                      transform: 'translateX(0%)'
                    },
                    'to': {
                      transform: data.title.length > 20 ? 
                        `translateX(calc(-100% + 200px))` : 'translateX(0%)'
                    }
                  }
                }}
              >
                {data.title}
              </Typography>
            </Box>
          </Tooltip>
          
          {data.dueDate && data.status?.toLowerCase() !== 'done' && (
            <Tooltip 
              title={
                isDatePast(data.dueDate, data.status) 
                  ? "Prazo vencido!" 
                  : isNearDeadline(data.dueDate, data.status) 
                    ? "Atenção: Prazo se aproximando do fim!" 
                    : "Data de entrega"
              } 
              arrow
            >
              <DeadlineContainer $status={getDeadlineStatus(data.dueDate, data.status)}>
                {isDatePast(data.dueDate, data.status) ? (
                  <ErrorIcon fontSize="inherit" color="error" />
                ) : isNearDeadline(data.dueDate, data.status) ? (
                  <WarningIcon fontSize="inherit" sx={{ color: '#f39c12' }} />
                ) : (
                  <AccessTimeIcon fontSize="inherit" />
                )}
                {formatDateString(data.dueDate)}
              </DeadlineContainer>
            </Tooltip>
          )}
        </Box>
        
        {/* Footer with priority, users and attachments */}
        <Footer>
          {/* Left side - Status and Priority */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {data.status && (
              <Tooltip title={`Status: ${getStatusLabel(data.status)}`} arrow>
                <StatusBadge
                  status={data.status}
                  label={getStatusLabel(data.status)}
                  size="small"
                />
              </Tooltip>
            )}
            {data.priority && (
              <Tooltip title={`Prioridade: ${getPriorityLabel(getSavedPriority(data.id, data.priority))}`} arrow>
                <PriorityBadge priority={getSavedPriority(data.id, data.priority)} />
              </Tooltip>
            )}
          </Box>
          
          {/* Right side - Users and Attachments */}
          <UserAttachmentsContainer>
            {/* Responsible Users */}
            {data.assignees && data.assignees.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {data.assignees.slice(0, 2).map((assignee, index) => (
                  <Tooltip key={assignee.id} title={`Responsável: ${assignee.name}`} arrow>
                    <AssigneeAvatar
                      src={assignee.avatar}
                      alt={assignee.name}
                      sx={{ marginLeft: index > 0 ? '-8px' : 0, zIndex: 3 - index }}
                    >
                      {assignee.name.substring(0, 1).toUpperCase()}
                    </AssigneeAvatar>
                  </Tooltip>
                ))}
                {data.assignees.length > 2 && (
                  <Tooltip title={`+${data.assignees.length - 2} responsáveis`} arrow>
                    <AssigneeAvatar
                      sx={{ marginLeft: '-8px', backgroundColor: 'rgba(255, 255, 255, 0.3)', color: '#fff' }}
                    >
                      +{data.assignees.length - 2}
                    </AssigneeAvatar>
                  </Tooltip>
                )}
              </Box>
            )}
            
            {/* Attachments */}
            <AttachmentsContainer>
              {data.attachmentCount && data.attachmentCount > 0 && (
                <Tooltip title="Anexos" arrow>
                  <MetadataItem>
                    <CountBadge badgeContent={data.attachmentCount}>
                      <AttachmentIcon fontSize="small" />
                    </CountBadge>
                  </MetadataItem>
                </Tooltip>
              )}
              
              {/* Comments count if available */}
              {data.commentCount && data.commentCount > 0 && (
                <Tooltip title="Comentários" arrow>
                  <MetadataItem>
                    <CountBadge badgeContent={data.commentCount}>
                      <CommentIcon fontSize="small" />
                    </CountBadge>
                  </MetadataItem>
                </Tooltip>
              )}
            </AttachmentsContainer>
          </UserAttachmentsContainer>
        </Footer>
      </CardContainer>

      {modalOpen && (
        <OpenCardModal
          open={modalOpen}
          onClose={handleModalClose}
          cardId={data.id}
          columnId={columnId}
          onCardUpdate={handleCardUpdate}
          isObserver={isObserver}
        />
      )}
    </>
  );
};

export default BoardCard;